import type {
  ApplicantTypeEnum,
  ApplicantWithRelationships,
  GenderEnum,
  RelationshipStatusEnum,
  RelationshipTypeEnum,
} from '@/types';
import { mapFromApi } from '@/mappers';
import type { ApplicantDto } from '@/api/backend/users/applications';

/**
 * The backend exposes two HasMany associations over the same applicant_relationships table:
 *  - `applicantRelationship` (singular, FK `applicantId`) — rows this applicant owns; the partner
 *    is in `relatedApplicantId`.
 *  - `applicantRelationships` (plural, FK `relatedApplicantId`) — rows naming this applicant; the
 *    partner is in `applicantId`.
 *
 * Some data only populates one side, so both are merged — keeping only the plural side leaves the
 * owner of a one-sided row looking single. This mirrors what the backend does in
 * `common/applicant-relationships.ts` and `calculator/v2/calcs-payload.utils.ts`.
 */
type RelationshipRows = NonNullable<ApplicantDto['applicantRelationships']>;

const mergeRelationshipRows = (data: ApplicantDto): RelationshipRows => {
  const merged: RelationshipRows = [];
  // The DTO carries no row id, so an edge is identified by what it means: the pair it joins and
  // the relationship type. That collapses the same row hydrated into both associations while
  // keeping a genuine reciprocal pair (whose ids are swapped) as two rows.
  const seen = new Set<string>();

  for (const row of [...(data.applicantRelationships ?? []), ...(data.applicantRelationship ?? [])]) {
    const key = `${row.relationship ?? ''}|${row.applicantId ?? ''}|${row.relatedApplicantId ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }

  return merged;
};

export const applicant = (data: ApplicantDto): ApplicantWithRelationships => {
  // Unchanged precedence for the scalar fields below: the plural side wins, falling back to the
  // singular one. Only `applicantRelationships` gains the merged rows.
  const relationships = data.applicantRelationships?.length
    ? data.applicantRelationships
    : data.applicantRelationship ?? [];

  return {
    id: data.id ?? '',
    type: data.applicantType as ApplicantTypeEnum,
    firstName: data.firstName ?? '',
    middleName: data.middleName ?? '',
    lastName: data.lastName ?? '',
    preferredName: data.preferredName ?? '',
    numberOfDependants: data.numberOfDependants ?? 0,
    gender: data.gender as GenderEnum,
    genderOther: '',
    birthdate: data.dateOfBirth ?? '',
    address: mapFromApi.address(data),
    mobile: data.mobilePhoneNumber ?? '',
    email: data.email ?? '',
    confirmEmail: data.email ?? '',
    relationshipStatus: data.relationshipStatus as RelationshipStatusEnum,
    relationshipToPrimary:
      data.applicantType === 'secondary' ? (relationships[0]?.relationship as RelationshipTypeEnum) : undefined,
    relationshipOtherInfo: relationships[0]?.otherRelationshipDescription ?? '',
    applicantRelationships: mergeRelationshipRows(data),
  };
};
